import React, { useEffect, useState } from "react";
import {
    Accordion, AccordionDetails, AccordionSummary,
    Chip, Divider, Grid, Icon, Stack, Tooltip, Typography
} from "@mui/material";


import { StepperEnum, useWizardStepperContext } from "../../../../contexts/useWizardStepper.context";
import useWizardReportDataContext from "../../../../contexts/useWizardReportData.context";
import IReportSelectedColumn from "../../../../../../../shared/contracts/IReportSelectedColumn";
import IDataSourceProps from "../../../../../../../shared/contracts/IDataSourceProps";
import MuiIcon from "../../../../../../../shared/styles/mui/helpers/mui-icon.helper";
import CallOutComponent from "../../../callout/callout.component";
import useHelpers from "../../../../../../../shared/services/helpers/useHelpers.hook";


interface IProps {

}
export default function WizardSummaryComponent({ }: IProps) {

    const { sortAlphabeticallyThenByLength } = useHelpers();
    const { activeStep } = useWizardStepperContext();
    const { wizardData, isNewReport, availableDataSources } = useWizardReportDataContext();

    const [parsedValues, setParsedValues] = useState<{
        dataSources?: IDataSourceProps[],
        fields?: IReportSelectedColumn[],
        emailRecipients?: string[],
    }>({});

    useEffect(() => {
        let fields = sortAlphabeticallyThenByLength('columnDescription', wizardData.reportSpecification.columnSelections);
        setParsedValues({
            emailRecipients: wizardData.emailRecipients 
            ? wizardData.emailRecipients.split(',').filter((email) => email.trim() !== '')
            : [],
            dataSources: availableDataSources.filter(x => x.dataSourceCode === wizardData.datasourceGuid) || [],
            fields: fields,

        });
    }, [wizardData])

    function formatDisplayDate(dateString:any) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); 
    const year = String(date.getFullYear()).slice(-2); 
    const time = date.toTimeString().split(' ')[0]; 
    return `${day}-${month}-${year}  ${time}`;
}

    return (<Grid container direction='column' alignItems='center' sx={{ pt: 0, pb: 0 }}
        hidden={(activeStep !== StepperEnum.Create) && (activeStep !== StepperEnum.Update)}
    >
        {/* Details */}
        <Grid item container sx={{ width: '800px' }} direction='row' justifyContent='space-between'>
            <Accordion elevation={0} disableGutters expanded variant="elevation" sx={{ backgroundColor: (t) => t.palette.background.paper, width: '100%' }}>
                <AccordionSummary sx={{ my: 0, py: 0 }} expandIcon={null} >
                    {<Icon sx={{ mr: 2 }} color="success">{MuiIcon.FINISH({ sx: { fontSize: '32px' } })}</Icon>}
                    <CallOutComponent type={'success'}
                        text={`You are almost ready to ${isNewReport ? 'Create' : 'Update'} the report. Please take a moment to review your changes.`} />
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: (t) => t.palette.background.paper }}>

                    <Divider />
                    <Grid item container sx={{ p: 1 }}>
                        <Grid xs={4} item>
                            <Typography color='text.secondary'>Report Name</Typography>
                        </Grid>
                        <Grid xs={8} item>
                            <Typography>{wizardData.reportName}</Typography>
                        </Grid>
                    </Grid>
                    {!isNewReport && <Grid item container sx={{ p: 1 }}>
                        <Grid xs={4} item sx={{ color: (t) => t.palette.text.secondary }}>Code</Grid>
                        <Grid xs={8} item>{wizardData.reportCode}</Grid>
                    </Grid>}
                    <Grid item container sx={{ p: 1 }}>
                        <Grid xs={4} item sx={{ color: (t) => t.palette.text.secondary }}>Description</Grid>
                        <Grid xs={8} item>{wizardData.reportDescription}</Grid>
                    </Grid>


                    <Divider />
                    {Array.isArray(parsedValues.emailRecipients) && parsedValues.emailRecipients.length > 0 && <Grid item container sx={{ p: 1 }}>
                        <Grid xs={4} item>
                            <Typography color='text.secondary'>Recipients</Typography>
                        </Grid>
                        <Grid xs={8} item>
                            <Stack direction="row" spacing={0} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {(parsedValues.emailRecipients).map((email, index) =>
                                    <Chip color="info" variant="outlined" sx={{ ml: 0 }} size="medium" key={'email_' + index} label={email} />
                                )}
                            </Stack>
                        </Grid>
                    </Grid>}

                    <Divider />
                    <Grid item container sx={{ p: 1 }}>
                        <Grid xs={4} item sx={{ color: (t) => t.palette.text.secondary }}>Bucket</Grid>
                        <Grid xs={8} item>{wizardData.bucket}</Grid>
                    </Grid>
                    <Grid item container sx={{ p: 1 }}>
                        <Grid xs={4} item sx={{ color: (t) => t.palette.text.secondary }}>Owner</Grid>
                        <Grid xs={8} item>{wizardData.owner}</Grid>
                    </Grid>

                    <Divider />
                    <Grid item container sx={{ p: 1 }}>
                        <Grid xs={4} item sx={{ color: (t) => t.palette.text.secondary }}>Category</Grid>
                        <Grid xs={8} item>{wizardData.category}</Grid>
                    </Grid>
                    <Grid item container sx={{ p: 1 }}>
                        <Grid xs={4} item sx={{ color: (t) => t.palette.text.secondary }}>Permission</Grid>
                        <Grid xs={8} item>{wizardData.permissions}</Grid>
                    </Grid>

                    <Grid item container sx={{ p: 1 }}>
                        <Grid xs={4} item sx={{ color: (t) => t.palette.text.secondary }}>Schedule Date</Grid>
                        <Grid xs={8} item>{formatDisplayDate(wizardData.scheduleDate)}</Grid>
                    </Grid>
                    <Grid item container sx={{ p: 1 }}>
                        <Grid xs={4} item sx={{ color: (t) => t.palette.text.secondary }}>Schedule Frequency</Grid>
                        <Grid xs={8} item>{wizardData.scheduleFrequency}</Grid>
                    </Grid>
                    <Divider />
                    <Grid item container sx={{ p: 1 }}>
                        <Grid xs={4} item>
                            <Typography variant="button">Data Sources</Typography>
                        </Grid>
                        <Grid xs={8} item>
                            {(parsedValues.dataSources || []).map((dataSource, index) =>
                                <Tooltip key={'ds_' + index} title={dataSource.dataSourceViewName + ' | ' + dataSource.dataSourceCode} placement='bottom'>
                                    <Chip color="secondary" variant="outlined" size="medium" label={dataSource.dataSourceName} />
                                </Tooltip>
                            )}
                        </Grid>
                    </Grid>

                    <Divider />
                    <Grid item container sx={{ p: 1 }}>
                        <Grid xs={4} item>
                            <Typography variant="button">Fields</Typography>
                        </Grid>
                        <Grid xs={8} item>
                            <Stack direction="row" spacing={0} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {(parsedValues.fields || []).map((field, index) =>
                                    <Tooltip key={'field_' + index} title={field.aggregateFunction + ' | ' + field.columnName} placement='bottom'>
                                        <Chip color="primary" variant="outlined" size="medium" label={field.columnDescription || field.columnName} />
                                    </Tooltip>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

        </Grid>

    </Grid >);
}
